import type {PendingAttachmentResult} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {DocumentAttachment, UploadFailure} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PollFailError} from '@Libraries/pollable-job.library.types';
import {ChatDocumentType, ChatDocumentUploadErrorCode} from '@Libraries/chat-document.types';
import {AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import {createPendingDocumentAttachment, getExtensionFromFile, MAX_ATTACHMENTS} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {FileUploadPipelineConfig, FileUploadPipelineState} from '@Components/ai-prompt-box/libraries/ai-prompt-box-file-upload.library';
import {handleFilesUpload, retryFileUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-file-upload.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {getUploadFileAjaxFunctionV2, makeUploadStatusChecker, runMediaUploadJob} from '@Libraries/user-media-library';

export interface ChatDocumentUploadResponse {
  documentHashedId: string;
}

interface PartitionedChatFiles {
  images: File[];
  documents: File[];
  unsupported: File[];
}

const MEGABYTE = 1024 * 1024;
const PDF_MAX_BYTES = 10 * MEGABYTE;
const OTHER_DOCUMENT_MAX_BYTES = 20 * MEGABYTE;

const getChatDocumentMaxBytes = (documentType: ChatDocumentType): number => (documentType === ChatDocumentType.PDF ? PDF_MAX_BYTES : OTHER_DOCUMENT_MAX_BYTES);

const DOCUMENT_TYPE_BY_EXTENSION: Record<string, ChatDocumentType> = {
  pdf: ChatDocumentType.PDF,
  csv: ChatDocumentType.CSV,
  txt: ChatDocumentType.TXT,
  xlsx: ChatDocumentType.XLSX,
  docx: ChatDocumentType.DOCX,
};

const CHAT_DOCUMENT_ACCEPT = Object.keys(DOCUMENT_TYPE_BY_EXTENSION).map((extension) => `.${extension}`);

const UPLOAD_LOADING_KEY = 'ai-chat-document-upload';

export const getChatDocumentType = (file: File): ChatDocumentType | null => DOCUMENT_TYPE_BY_EXTENSION[getExtensionFromFile(file)] ?? null;

export const partitionChatFiles = (files: File[], acceptsDocuments: boolean): PartitionedChatFiles => {
  const result: PartitionedChatFiles = {images: [], documents: [], unsupported: []};
  files.forEach((file) => {
    if (AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      result.images.push(file);
      return;
    }
    const documentType = getChatDocumentType(file);
    if (acceptsDocuments && documentType !== null) {
      result.documents.push(file);
      return;
    }
    result.unsupported.push(file);
  });

  return result;
};

export const getChatFileAcceptList = (acceptsDocuments: boolean): string[] => [...AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES, ...(acceptsDocuments ? CHAT_DOCUMENT_ACCEPT : [])];

export const getChatFileAcceptAttribute = (acceptsDocuments: boolean): string => getChatFileAcceptList(acceptsDocuments).join(',');

const UPLOAD_FAILURE_KEYS: Record<ChatDocumentUploadErrorCode, {message: string; summary: string}> = {
  [ChatDocumentUploadErrorCode.INVALID_TYPE]: {message: 'pmwjs_ai_chat_document_error_invalid_type', summary: 'pmwjs_ai_chat_document_failed_not_supported'},
  [ChatDocumentUploadErrorCode.TOO_LARGE]: {message: 'pmwjs_ai_chat_document_error_too_large', summary: 'pmwjs_ai_chat_document_failed_too_large'},
  [ChatDocumentUploadErrorCode.TOO_MANY_PAGES]: {message: 'pmwjs_ai_chat_document_error_too_many_pages', summary: 'pmwjs_ai_chat_document_failed_too_many_pages'},
  [ChatDocumentUploadErrorCode.TOO_MANY_SHEETS]: {message: 'pmwjs_ai_chat_document_error_too_many_sheets', summary: 'pmwjs_ai_chat_document_failed_too_much_content'},
  [ChatDocumentUploadErrorCode.TOO_MANY_ROWS]: {message: 'pmwjs_ai_chat_document_error_too_many_rows', summary: 'pmwjs_ai_chat_document_failed_too_much_content'},
  [ChatDocumentUploadErrorCode.TOO_MANY_CELLS]: {message: 'pmwjs_ai_chat_document_error_too_many_cells', summary: 'pmwjs_ai_chat_document_failed_too_much_content'},
  [ChatDocumentUploadErrorCode.PASSWORD_PROTECTED]: {message: 'pmwjs_ai_chat_document_error_password_protected', summary: 'pmwjs_ai_chat_document_failed_password_protected'},
  [ChatDocumentUploadErrorCode.UNREADABLE]: {message: 'pmwjs_ai_chat_document_error_unreadable', summary: 'pmwjs_ai_chat_document_failed_unreadable'},
  [ChatDocumentUploadErrorCode.DAILY_LIMIT]: {message: 'pmwjs_ai_chat_document_error_daily_limit', summary: 'pmwjs_ai_chat_document_failed_daily_limit'},
  [ChatDocumentUploadErrorCode.UPLOAD_FAILED]: {message: 'pmwjs_ai_chat_document_error_upload_failed', summary: 'pmwjs_ai_chat_document_failed_upload_failed'},
};

const isChatDocumentUploadErrorCode = (code: string): code is ChatDocumentUploadErrorCode =>
  Object.values<string>(ChatDocumentUploadErrorCode).includes(code);

const toKnownErrorCode = (code: string): ChatDocumentUploadErrorCode => (isChatDocumentUploadErrorCode(code) ? code : ChatDocumentUploadErrorCode.UPLOAD_FAILED);

const getChatDocumentUploadFailureMessage = (code: string, documentType: ChatDocumentType): string => {
  const knownCode = toKnownErrorCode(code);
  const isPasswordProtectedPdf = knownCode === ChatDocumentUploadErrorCode.PASSWORD_PROTECTED && documentType === ChatDocumentType.PDF;

  return window.i18next.t(isPasswordProtectedPdf ? 'pmwjs_ai_chat_document_error_password_protected_pdf' : UPLOAD_FAILURE_KEYS[knownCode].message);
};

const describeUploadFailure = (error: unknown): UploadFailure => {
  const refusal = error instanceof Error && error.cause instanceof PollFailError ? {code: toKnownErrorCode(error.cause.code), message: error.message} : null;
  const code = refusal?.code ?? ChatDocumentUploadErrorCode.UPLOAD_FAILED;

  return {
    summary: window.i18next.t(UPLOAD_FAILURE_KEYS[code].summary),
    message: refusal?.message ?? window.i18next.t(UPLOAD_FAILURE_KEYS[code].message),
    isRetryable: code === ChatDocumentUploadErrorCode.UPLOAD_FAILED,
  };
};

const uploadChatDocument = (file: File, documentType: ChatDocumentType, onAccepted: VoidFunction): Promise<ChatDocumentUploadResponse> => {
  const formData = new FormData();
  formData.append('Filedata', file);

  return runMediaUploadJob<ChatDocumentUploadResponse>({
    kickoff: () => getUploadFileAjaxFunctionV2(formData, UPLOAD_LOADING_KEY, 'marketingagent/uploadDocument'),
    status: makeUploadStatusChecker<ChatDocumentUploadResponse>('marketingagent/getDocumentUploadStatus'),
    getFailureMessage: (code) => getChatDocumentUploadFailureMessage(code, documentType),
    onAck: onAccepted,
  });
};

const getDocumentTypeOfFile = (file: File): ChatDocumentType => {
  const documentType = getChatDocumentType(file);
  if (documentType === null) {
    throw new Error(`${file.name} is not a chat document`);
  }

  return documentType;
};

const buildDocumentUploadConfig = (documentTypeOf: (file: File) => ChatDocumentType): FileUploadPipelineConfig<ChatDocumentUploadResponse, DocumentAttachment> => ({
  maxAttachments: MAX_ATTACHMENTS,
  createPendingAttachment: (file): PendingAttachmentResult<DocumentAttachment> => createPendingDocumentAttachment(file, documentTypeOf(file)),
  uploadFile: (file, onAccepted): Promise<ChatDocumentUploadResponse> => uploadChatDocument(file, documentTypeOf(file), onAccepted),
  describeUploadFailure,
  resolveAttachment: (response, pending): DocumentAttachment => ({...pending, documentHashedId: response.documentHashedId, localId: undefined, pendingUpload: undefined}),
  onUploadFailed: (error): void => {
    showMessageGrowl({text: error instanceof Error ? error.message : window.i18next.t('pmwjs_ai_chat_document_error_upload_failed'), type: GROWL_TYPE.DANGER});
  },
});

const showDocumentTooLargeGrowl = (): void => {
  showMessageGrowl({text: window.i18next.t('pmwjs_ai_chat_document_error_too_large'), type: GROWL_TYPE.DANGER, showCloseIcon: true, interval: 7000});
};

export const handleDocumentFilesUpload = (allDocuments: File[], state: FileUploadPipelineState): number => {
  const documents = allDocuments.filter((file) => file.size <= getChatDocumentMaxBytes(getDocumentTypeOfFile(file)));
  if (documents.length < allDocuments.length) {
    showDocumentTooLargeGrowl();
  }
  if (documents.length === 0) {
    return 0;
  }

  return handleFilesUpload(documents, state, buildDocumentUploadConfig(getDocumentTypeOfFile));
};

export const retryPendingDocumentUpload = (localId: string, pending: DocumentAttachment, state: FileUploadPipelineState): void => {
  retryFileUpload(localId, pending, state, buildDocumentUploadConfig(() => pending.documentType));
};
